// unlock-killed-worker-marker.ts
//
// Req 9 (phase 05, NEW-3): `autopilot unlock` writes this marker whenever it
// actually killed a live worker process group — so the orphan reconciler's
// `supervisor_crash` evidence (no binding file at all) can tell "worker
// never spawned" apart from "worker spawned, then unlock killed it and
// cleaned up the binding" (measured: mission D2's `unlock --json` returned
// `killed_worker_pgid`, then the next `run` found no binding). Bounded
// lifetime: deleted once the reconciler's `attempt_recovered` for the
// ticket has committed (never reused for a LATER attempt on the same
// ticket) — see orphaned-ticket-recovery-applier.ts's cleanup call.
//
// Review H1 fix (code-review-phase-05.md): the marker is MISSION-WIDE
// (keyed by controlRoot, not ticket/attempt) — a naive "marker present =>
// name unlock" would mis-attribute the kill to a LATER ticket that crashed
// on its own after the unlock happened. `released_at` is now the timestamp
// the reconciler compares against the ticket's own most recent
// `attempt_started` (see orphaned-ticket-reconciler.ts). `ticket` is
// populated only when the caller can name which ticket's worker was killed
// (today `active-worker.json` carries no ticket id, so this stays
// `undefined` in practice until that record grows one — kept optional so
// the reconciler already does the stricter, ticket-matching check the
// moment it becomes available, no second migration needed).
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";

export interface UnlockKilledWorkerMarker {
  killed_worker_pgid: number;
  released_at: string;
  /** Ticket the killed worker was attempting, when known (see header). */
  ticket?: string;
}

function markerPath(controlRoot: string): string {
  return join(controlRoot, "unlock-killed-worker.json");
}

export async function writeUnlockKilledWorkerMarker(
  controlRoot: string,
  marker: UnlockKilledWorkerMarker,
): Promise<void> {
  await writeFileDurable(
    markerPath(controlRoot),
    Buffer.from(`${JSON.stringify(marker, null, 2)}\n`, "utf8"),
  );
}

/** `undefined` when no marker on disk — never throws (fail-soft, same as every other reconciler read). */
export async function readUnlockKilledWorkerMarker(
  controlRoot: string,
): Promise<UnlockKilledWorkerMarker | undefined> {
  const path = markerPath(controlRoot);
  if (!(await pathExists(path))) return undefined;
  try {
    const parsed = await readJson<Partial<UnlockKilledWorkerMarker>>(path);
    if (typeof parsed.killed_worker_pgid !== "number" || typeof parsed.released_at !== "string") {
      return undefined;
    }
    return {
      killed_worker_pgid: parsed.killed_worker_pgid,
      released_at: parsed.released_at,
      ...(typeof parsed.ticket === "string" ? { ticket: parsed.ticket } : {}),
    };
  } catch {
    return undefined;
  }
}

export async function deleteUnlockKilledWorkerMarker(controlRoot: string): Promise<void> {
  const { promises: fs } = await import("node:fs");
  await fs.rm(markerPath(controlRoot), { force: true });
}
